import { motionAllowed } from '../../lib/motion';

// The speech graph's bars, as it drew them (speech-graph.ts).
const BAR_WIDTH = 3;
const BAR_GAP = 2;
const STEP = BAR_WIDTH + BAR_GAP;
/** The speech graph fades older speech out over this much of its left edge. */
const FADE = 40;
/** Quieter than this is a pause. */
const VOICED = 0.12;
/** At Stop, the speech squeezes into a voice note at the left, as a messaging app shows one,
 * with the recording's length at its right. Its bars stand for the speech, each one the loudest
 * of the stretch it stands for: one for about every 14 px of room, between these many. */
const NOTE_BARS = { fewest: 10, most: 18 };
const NOTE_WIDTH = { least: 96, most: 140 };
const NOTE_BAR_WIDTH = 2.5;
/** The note's bars start this far in, and the length takes this much of its right end. */
const NOTE_INSET = 11;
const NOTE_END = 39;
/** The note's loudest bar, as a share of its height. */
const NOTE_LOUDEST = 0.625;
/** It squeezes in as the app's entrances arrive (--ease-out), and the length fades in after. */
const SQUEEZE_MS = 700;
const LENGTH_IN = { after: 280, over: 300 };
/** Then the AI tools' light runs through its bars, again and again, while the speech is read:
 * each bar it reaches lights up and grows a little, then cools over the next few. */
const READ = { after: 650, every: 1_500, trail: 7, grow: 0.14 };
/** Speech too quiet to show stands in as a made-up voice, so the note never looks empty. */
const MADE_UP_VOICE = (bar: number) => 0.3 + 0.5 * Math.abs(Math.sin((bar + 1) * 1.7));

/** Where the speech graph was on the page at Stop: its bars carry on from there. */
export interface SpeechPlace {
  readonly left: number;
  readonly width: number;
}

type Channels = readonly [number, number, number];

/** The curve of a CSS cubic-bezier() timing function. */
function cubicBezier(x1: number, y1: number, x2: number, y2: number): (t: number) => number {
  const along = (a: number, b: number, s: number) =>
    3 * a * s * (1 - s) ** 2 + 3 * b * s * s * (1 - s) + s ** 3;
  return (t) => {
    if (t <= 0) return 0;
    if (t >= 1) return 1;
    // The curve's parameter at this time, by halving, then its value there.
    let low = 0;
    let high = 1;
    for (let step = 0; step < 20; step++) {
      const middle = (low + high) / 2;
      if (along(x1, x2, middle) < t) low = middle;
      else high = middle;
    }
    return along(y1, y2, (low + high) / 2);
  };
}
const easeOut = cubicBezier(0.16, 1, 0.3, 1);

const clamp = (value: number) => Math.min(1, Math.max(0, value));
const lerp = (from: number, to: number, amount: number) => from + (to - from) * amount;

/** `color` as the canvas reads it, or `fallback` where it can't. */
function canvasColor(paint: CanvasRenderingContext2D, color: string, fallback: string): string {
  if (!color.trim()) return fallback;
  paint.fillStyle = fallback;
  paint.fillStyle = color.trim();
  return typeof paint.fillStyle === 'string' ? paint.fillStyle : fallback;
}

/** A colour as the canvas gives it (#rrggbb, or rgb() or rgba()), as its red, green and blue. */
function channels(color: string): Channels {
  const hex = /^#([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(color);
  const parts = hex
    ? hex.slice(1, 4).map((channel) => parseInt(channel, 16))
    : (/^rgba?\(([\d.]+),\s*([\d.]+),\s*([\d.]+)/.exec(color)?.slice(1, 4).map(Number) ?? []);
  return [parts[0] ?? 0, parts[1] ?? 0, parts[2] ?? 0];
}
const mix = (from: Channels, to: Channels, amount: number): Channels => [
  lerp(from[0], to[0], amount),
  lerp(from[1], to[1], amount),
  lerp(from[2], to[2], amount),
];
const css = ([red, green, blue]: Channels) =>
  `rgb(${String(Math.round(red))}, ${String(Math.round(green))}, ${String(Math.round(blue))})`;

interface Bar {
  /** Where the speech graph drew it, in this canvas. */
  readonly x: number;
  readonly height: number;
  /** How faded it was, at the speech graph's left edge. */
  readonly faded: number;
  /** The note's bar it joins. */
  readonly into: number;
}

/**
 * Draws the recording as a voice note on `canvas` while its transcript comes: the speech graph's
 * bars, from where it drew them (`from`, else as if it filled this canvas), squeeze into a voice
 * note with the recording's `length` beside them, and a light runs through its bars, again and
 * again, as it's read. The speech is the recording's own `levels` (the speech graph's). Without
 * motion, it's the note alone, holding still. Returns the function that stops it.
 */
export function drawTranscript(
  canvas: HTMLCanvasElement,
  levels: readonly number[],
  { length, from }: { readonly length: string; readonly from?: SpeechPlace | undefined },
): () => void {
  // jsdom can't draw.
  if (typeof window.matchMedia === 'undefined') return () => undefined;
  const paint = canvas.getContext('2d');
  if (!paint) return () => undefined;
  const style = getComputedStyle(canvas);
  const speech = channels(canvasColor(paint, style.color, '#2dd4bf'));
  const light = channels(
    canvasColor(paint, style.getPropertyValue('--transcript-light'), css(speech)),
  );
  const ground = channels(
    canvasColor(paint, style.getPropertyValue('--transcript-ground'), '#19191e'),
  );
  const noteFill = canvasColor(paint, style.getPropertyValue('--transcript-note'), 'transparent');
  const noteEdge = canvasColor(
    paint,
    style.getPropertyValue('--transcript-note-edge'),
    'transparent',
  );
  const lengthColor = canvasColor(
    paint,
    style.getPropertyValue('--transcript-length'),
    css(speech),
  );
  // The note's bars wait in the speech's colour, dimmed; the light lights them.
  const waiting = mix(speech, ground, 0.58);
  // Browsers without rounded rectangles (Safari before 16) draw square ones instead.
  const rounded = typeof (paint as Partial<typeof paint>).roundRect === 'function';
  const pill = (x: number, y: number, width: number, height: number) => {
    if (rounded) paint.roundRect(x, y, width, height, Math.min(width, height) / 2);
    else paint.rect(x, y, width, height);
  };
  const moving = motionAllowed();
  // Laid out once the canvas has a size.
  let note: { width: number; loudness: number[]; bars: Bar[] } | undefined;
  let started: number | undefined;
  let frame = 0;

  const layOut = (width: number, height: number) => {
    // The bars the speech graph showed last, at its own place: the newest at its right edge.
    const shownWidth = from?.width ?? width;
    const offset = from ? from.left - canvas.getBoundingClientRect().left : 0;
    const count = Math.floor((shownWidth + BAR_GAP) / STEP);
    const shown = Array.from({ length: count }, (_, bar) => ({
      level: levels[levels.length - count + bar] ?? 0,
      x: offset + shownWidth - (count - bar) * STEP + BAR_GAP,
      faded: clamp((shownWidth - (count - bar) * STEP + BAR_GAP) / FADE),
    })).filter(({ x }) => x > -BAR_WIDTH && x < width);
    const noteWidth = Math.min(
      width,
      Math.max(NOTE_WIDTH.least, Math.min(NOTE_WIDTH.most, Math.floor(width / 14) * 4 + 52)),
    );
    const room = noteWidth - NOTE_INSET - NOTE_END;
    const barCount = Math.max(
      2,
      Math.min(
        NOTE_BARS.most,
        Math.max(NOTE_BARS.fewest, Math.floor(width / 14)),
        Math.floor(room / (NOTE_BAR_WIDTH + 1)) + 1,
      ),
    );
    const loudness: number[] = Array.from({ length: barCount }, () => 0);
    const bars = shown.map(({ level, x, faded }, index) => {
      const into = Math.min(barCount - 1, Math.floor((index * barCount) / shown.length));
      loudness[into] = Math.max(loudness[into] ?? 0, level);
      return { x, height: Math.max(BAR_WIDTH, level * height), faded, into };
    });
    const heard = loudness.some((level) => level >= VOICED);
    note = {
      width: noteWidth,
      loudness: heard ? loudness : loudness.map((_, bar) => MADE_UP_VOICE(bar)),
      bars,
    };
  };

  const draw = (time: number) => {
    started ??= time;
    const elapsed = time - started;
    const ratio = window.devicePixelRatio || 1;
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    if (canvas.width !== Math.round(width * ratio)) canvas.width = Math.round(width * ratio);
    if (canvas.height !== Math.round(height * ratio)) canvas.height = Math.round(height * ratio);
    paint.setTransform(ratio, 0, 0, ratio, 0, 0);
    paint.clearRect(0, 0, width, height);
    if (width > 0 && height > 0) {
      if (!note) layOut(width, height);
      const { width: noteWidth, loudness, bars } = note ?? { width: 0, loudness: [], bars: [] };
      const squeezed = moving ? easeOut(clamp(elapsed / SQUEEZE_MS)) : 1;
      const middle = height / 2;

      paint.globalAlpha = squeezed;
      paint.fillStyle = noteFill;
      paint.strokeStyle = noteEdge;
      paint.lineWidth = 1;
      paint.beginPath();
      pill(0.5, 0.5, noteWidth - 1, height - 1);
      paint.fill();
      paint.stroke();
      paint.globalAlpha = moving ? clamp((elapsed - LENGTH_IN.after) / LENGTH_IN.over) : 1;
      paint.fillStyle = lengthColor;
      paint.font = `600 11px ${style.fontFamily}`;
      paint.textAlign = 'right';
      paint.textBaseline = 'middle';
      paint.fillText(length, noteWidth - 10, middle + 0.5);

      // Where the light is among the note's bars, from just before the first to past the last.
      const reading = elapsed - READ.after;
      const lightAt =
        moving && reading >= 0
          ? ((reading % READ.every) / READ.every) * (loudness.length + READ.trail + 3) - 3
          : -Infinity;
      const room = noteWidth - NOTE_INSET - NOTE_END;
      const noteBar = (into: number) => {
        const lit = lightAt >= into ? clamp(1 - (lightAt - into) / READ.trail) : 0;
        return {
          x: NOTE_INSET + (into * room) / Math.max(1, loudness.length - 1),
          height:
            Math.max(NOTE_BAR_WIDTH, (loudness[into] ?? 0) * height * NOTE_LOUDEST) *
            (1 + READ.grow * lit),
          lit,
        };
      };
      const drawBar = (x: number, barWidth: number, barHeight: number, color: Channels) => {
        paint.fillStyle = css(color);
        paint.beginPath();
        pill(x, middle - barHeight / 2, barWidth, barHeight);
        paint.fill();
      };
      if (squeezed < 1) {
        // Every bar of the speech moves into its place in the note, and takes its colour.
        for (const bar of bars) {
          const target = noteBar(bar.into);
          paint.globalAlpha = lerp(bar.faded, 1, squeezed);
          drawBar(
            lerp(bar.x, target.x, squeezed),
            lerp(BAR_WIDTH, NOTE_BAR_WIDTH, squeezed),
            lerp(bar.height, target.height, squeezed),
            mix(mix(speech, waiting, squeezed), light, target.lit),
          );
        }
        paint.globalAlpha = 1;
      } else {
        // Squeezed, the speech's bars are on top of each other: each of the note's is drawn once.
        loudness.forEach((_, into) => {
          const target = noteBar(into);
          drawBar(target.x, NOTE_BAR_WIDTH, target.height, mix(waiting, light, target.lit));
        });
      }
    }
    if (moving) frame = requestAnimationFrame(draw);
  };
  frame = requestAnimationFrame(draw);
  return () => {
    cancelAnimationFrame(frame);
  };
}
